import type { SessionAuthMethod } from "@repo/app-types/api/user";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Link } from "@repo/ui/Link/index";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { IconCalendar } from "@tabler/icons-react";
import type { FC } from "react";
import { AuthMethodOptions } from "../signin/authMethodOptions.js";
import { OnboardingShell } from "./onboardingShell.js";

/**
 * Presentational screens for the account-resolution flow (unresolved sessions).
 * Each is a pure function of its props — all data fetching and side effects are
 * supplied by the `AccountResolution` container, so these render standalone in
 * Storybook.
 */

function ErrorText({ message }: { message: string | null }) {
  if (!message) {
    return null;
  }
  return <p className="mt-3 text-center text-sm text-destructive">{message}</p>;
}

/**
 * Full-screen spinner shown while the SSO join auto-fires. Surfaces an inline
 * error (with a sign-out escape hatch) when the join fails.
 */
export const SsoInterstitialScreen: FC<{
  companyName: string;
  errorMessage: string | null;
  onSignOut: () => void;
}> = ({ companyName, errorMessage, onSignOut }) => {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4">
      <Loader style={LoaderStyle.ZOOMIES} />
      <div className="text-center text-sm text-muted-foreground">
        {`Signing you in to ${companyName}…`}
      </div>
      {errorMessage ? (
        <div className="max-w-md text-center text-sm text-destructive">
          {errorMessage}{" "}
          <button
            className="font-medium text-blue-600 hover:text-blue-500"
            onClick={onSignOut}
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
};

interface SessionExpiredScreenProps {
  companyName: string;
  /** Whether signing out the expired session failed, leaving it in place. */
  hasSignOutFailed: boolean;
  onRetry: () => void;
}

/**
 * Shown while an expired session is signed out and sent back through sign-in,
 * or with a retry when the sign-out failed.
 */
export const SessionExpiredScreen: FC<SessionExpiredScreenProps> = (props) => {
  const { companyName, hasSignOutFailed, onRetry } = props;
  if (hasSignOutFailed) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <div className="max-w-md text-center text-sm text-muted-foreground">
          Your {companyName} session has expired, but we couldn&apos;t sign you
          out.
        </div>
        <Button variant={ButtonVariant.Primary} onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4">
      <Loader style={LoaderStyle.ZOOMIES} />
      <div className="text-center text-sm text-muted-foreground">
        Your {companyName} session has expired. Taking you to sign in again…
      </div>
    </div>
  );
};

/**
 * Shown when the teammate belongs to a company that no longer accepts the
 * current session's sign-in method. Directs them to re-authenticate with a
 * method the company does accept, rendering only those methods via the shared
 * {@link AuthMethodOptions} (SSO one-click when configured, else Google /
 * Microsoft). SSO-only companies with no launchable provider fall back to the
 * sign-out escape hatch in {@link OnboardingShell}.
 */
export const AuthMethodRequiredScreen: FC<{
  companyName: string;
  acceptedMethods: readonly SessionAuthMethod[];
  ssoProviderIds: readonly string[];
  errorMessage: string | null;
  onGoogleSignIn: () => void;
  onMicrosoftSignIn: () => void;
  onSsoSignIn: () => void;
  onSignOut: () => void;
}> = ({
  companyName,
  acceptedMethods,
  ssoProviderIds,
  errorMessage,
  onGoogleSignIn,
  onMicrosoftSignIn,
  onSsoSignIn,
  onSignOut,
}) => {
  const isSsoOnly = !acceptedMethods.includes("oauth");
  return (
    <OnboardingShell onSignOut={onSignOut}>
      <h1 className="mb-2 text-center text-2xl font-semibold text-foreground">
        {isSsoOnly
          ? "Use single sign-on to continue"
          : "Use Google or Microsoft to continue"}
      </h1>
      <p className="text-center text-sm text-gray-600">
        {isSsoOnly
          ? `${companyName} requires signing in with SSO. Please sign in again using single sign-on.`
          : `${companyName} requires signing in with a Google or Microsoft account. Please sign in again to continue.`}
      </p>
      <ErrorText message={errorMessage} />
      <div className="mt-6 flex flex-col items-center space-y-4">
        <AuthMethodOptions
          acceptedMethods={acceptedMethods}
          ssoProviderIds={ssoProviderIds}
          onGoogleSignIn={onGoogleSignIn}
          onMicrosoftSignIn={onMicrosoftSignIn}
          onSsoSignIn={onSsoSignIn}
        />
      </div>
    </OnboardingShell>
  );
};

/** Accept a pending invitation to join a company. */
export const AcceptInvitationScreen: FC<{
  companyName?: string;
  isJoining: boolean;
  errorMessage: string | null;
  onAccept: () => void;
  onSignOut: () => void;
}> = ({ companyName, isJoining, errorMessage, onAccept, onSignOut }) => {
  return (
    <OnboardingShell onSignOut={onSignOut}>
      <h1 className="mb-2 text-center text-2xl font-semibold text-foreground">
        {companyName
          ? `Join the ${companyName} team on Alpharun`
          : "Accept your invitation"}
      </h1>
      <p className="text-center text-sm text-gray-600">
        Accept your invitation to get started.
      </p>
      <ErrorText message={errorMessage} />
      <Button
        className="mx-auto mt-6"
        variant={ButtonVariant.Primary}
        onClick={onAccept}
        isDisabled={isJoining}
      >
        {isJoining ? "Joining…" : "Accept invitation"}
      </Button>
    </OnboardingShell>
  );
};

/** Join the company that owns the email's domain. */
export const JoinDomainScreen: FC<{
  companyName: string;
  isJoining: boolean;
  errorMessage: string | null;
  onJoin: () => void;
  onUseDifferentAccount: () => void;
}> = ({
  companyName,
  isJoining,
  errorMessage,
  onJoin,
  onUseDifferentAccount,
}) => {
  return (
    <OnboardingShell
      onSignOut={onUseDifferentAccount}
      signOutLabel="Use a different account"
    >
      <h1 className="mb-2 text-center text-2xl font-semibold text-foreground">
        {`Join ${companyName}`}
      </h1>
      <p className="text-center text-sm text-gray-600">
        {`Your email matches the ${companyName} organization on Alpharun.`}
      </p>
      <ErrorText message={errorMessage} />
      <Button
        className="mx-auto mt-6"
        variant={ButtonVariant.Primary}
        onClick={onJoin}
        isDisabled={isJoining}
      >
        {isJoining ? "Joining…" : `Join ${companyName}`}
      </Button>
    </OnboardingShell>
  );
};

/** Last resort when nothing resolves — point the user at booking a demo. */
export const NoOrganizationScreen: FC<{
  onScheduleDemo: () => void;
  onSignOut: () => void;
}> = ({ onScheduleDemo, onSignOut }) => {
  return (
    <OnboardingShell onSignOut={onSignOut}>
      <h1 className="mb-2 text-center text-2xl font-semibold text-foreground">
        Welcome to Alpharun
      </h1>
      <p className="text-center text-sm text-gray-600">
        Your account isn&apos;t connected to an organization yet. Book a demo to
        get started, or ask a teammate to invite you.
      </p>
      <div className="mt-6 flex flex-col gap-3">
        <div className="mx-auto w-full max-w-[300px]">
          <Button
            variant={ButtonVariant.Primary}
            onClick={onScheduleDemo}
            icon={<IconCalendar className="h-5 w-5" />}
            className="w-full"
            size={Size.LARGE}
          >
            Schedule a demo
          </Button>
        </div>
        <div className="text-center text-sm text-muted-foreground">
          Questions? Email us at{" "}
          <Link href="mailto:hello@alpharun.com">hello@alpharun.com</Link>.
        </div>
      </div>
    </OnboardingShell>
  );
};
